import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Avatar,
    Badge,
    Box,
    Flex,
    Icon,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { FaRegEdit } from 'react-icons/fa';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdOutlineMiscellaneousServices } from 'react-icons/md';
import { useDiscount } from 'contexts/DiscountContext';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Card from 'components/card/Card';
import CButton from 'components/CButton';
import Pagination from 'components/Pagination';
import ConfirmDialog from 'components/ConfirmDialog';

export default function DiscountCategoriesOverview({ canCreate, canRead, canUpdate, canDelete }) {
    const { getAllCategories, deleteCategory } = useDiscount();

    const textColor = useColorModeValue('navy.700', 'white');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1,
        activeOnly: false
    });
    const navigate = useNavigate();

    const handleDelete = async () => {
        if (categoryToDelete) {
            try {
                await deleteCategory(categoryToDelete);
                setCategoryToDelete(null);
                fetchCategories();
            } catch (error) {
                console.error('Failed to delete service category:', error);
            }
            setIsDialogOpen(false);
        }
    };

    const openDeleteDialog = (serviceId) => {
        setCategoryToDelete(serviceId);
        setIsDialogOpen(true);
    };

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [columnHelper.accessor('icon', {
            header: () => <Text color="gray.400">ICON</Text>,
            cell: (info) => (
                <Flex align="center">
                    <Avatar
                        h="48px"
                        w="48px"
                        bg="#fa8f03"
                        color="white"
                        icon={<MdOutlineMiscellaneousServices />}
                        src={info.getValue()}
                    />
                </Flex>
            ),
        }),
        columnHelper.accessor('name', {
            header: () => <Text color="gray.400">NAME</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('is_active', {
            header: () => <Text color="gray.400">STATUS</Text>,
            cell: (info) => (
                <Badge colorScheme={info.getValue() === 1 ? 'green' : 'red'} fontSize="sm" fontWeight="500">
                    {info.getValue() === 1 ? 'Active' : 'Inactive'}
                </Badge>
            ),
        }),]

        if (canUpdate || canDelete) {
            baseColumns.push(
                columnHelper.accessor('actions', {
                    header: () => <Text color="gray.400">ACTIONS</Text>,
                    cell: (info) => (
                        <Flex>
                            {canUpdate && <Icon
                                as={FaRegEdit}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                onClick={() => navigate(`/admin/discount/category/edit/${info.row.original.id}`)}
                                cursor='pointer'
                                title="Edit"
                                color='#fa8f03'
                            />}
                            {
                                canDelete && <Icon
                                    as={RiDeleteBin6Line}
                                    h={6}
                                    w={6}
                                    mt='10px'
                                    me='20px'
                                    onClick={() => openDeleteDialog(info.row.original.id)}
                                    cursor='pointer'
                                    title="Delete"
                                    color='#fa8f03'
                                />
                            }
                        </Flex>
                    ),
                }),
            );
        }
        return baseColumns;
    }, [canUpdate, canDelete]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        fetchCategories();
    }, [filter.search, filter.limit, filter.page]);


    const fetchCategories = async () => {
        setIsLoading(true);
        try {
            const data = await getAllCategories(filter);
            if (data && data.categories) {
                setData(data.categories);
                setTotalPages(data.totalPages || 0);
            } else {
                console.warn('No service categories found or data is undefined');
                setData([]);
            }
        } catch (error) {
            console.error('Failed to fetch service categories:', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    return (
        <Flex direction="column" pt={{ sm: '125px', lg: '55px' }}>
            <ConfirmDialog
                position="top"
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleDelete}
                header="Delete Confirmation"
                message="Are you sure you want to delete this category?"
            />
            {canRead &&
                <Card px="0px" minHeight="80vh">
                    <Flex direction="column" w="100%" overflowX="auto">
                        <Flex
                            align="center"
                            justify="space-between"
                            w="100%"
                            px="22px"
                            mb="36px"
                        >
                            <DebouncedInput
                                value={globalFilter ?? ''}
                                onChange={(value) =>
                                    setFilter((prev) => ({ ...prev, search: value, page: 1 }))
                                }
                                placeholder="Search..."
                            />

                            <Flex gap="16px">
                                {canCreate &&
                                    <CButton
                                        onClick={() => navigate('/admin/discount/category/create')}
                                    >
                                        Add Category
                                    </CButton>
                                }
                            </Flex>
                        </Flex>
                        {
                            isLoading ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    borderRadius="10px"
                                    overflow="hidden"
                                    minHeight="500px"
                                >
                                    <Spinner color="#fa8f03" />
                                </Box>
                            ) : (
                                <>
                                    <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                                        <Table variant="simple">
                                            <Thead>
                                                {table.getHeaderGroups().map((headerGroup) => (
                                                    <Tr key={headerGroup.id} bg='#fa8f03'>
                                                        {headerGroup.headers.map((header) => (
                                                            <Th key={header.id} borderColor='#fa8f03'>
                                                                {header.isPlaceholder ? null : (
                                                                    <Flex
                                                                        onClick={header.column.getToggleSortingHandler()}
                                                                        cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                        justify="space-between"
                                                                        align="center"
                                                                        color="white"
                                                                    >
                                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    </Flex>
                                                                )}
                                                            </Th>
                                                        ))}
                                                    </Tr>
                                                ))}
                                            </Thead>
                                            <Tbody>
                                                {isLoading ? (
                                                    <Tr>
                                                        <Td colSpan={columns.length} textAlign="center" height="500px">
                                                            <Flex justify="center" align="center" height="100%">
                                                                <Spinner />
                                                            </Flex>
                                                        </Td>
                                                    </Tr>
                                                ) : data.length > 0 ? (
                                                    table.getRowModel().rows.map((row) => (
                                                        <Tr key={row.id}>
                                                            {row.getVisibleCells().map((cell) => (
                                                                <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </Td>
                                                            ))}
                                                        </Tr>
                                                    ))
                                                ) : (
                                                    <Tr>
                                                        <Td colSpan={columns.length} textAlign="center">
                                                            No categories found.
                                                        </Td>
                                                    </Tr>
                                                )}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                    <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                                </>
                            )}
                    </Flex>
                </Card>
            }
        </Flex>
    );
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <SearchBar
            value={value}
            onChange={(e) => setValue(e.target.value)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
            {...props}
        />
    );
}
